import React, { useMemo, useRef, useState } from 'react';
import { useMemoizedFn } from 'ahooks';
import dayjs from 'dayjs';
import { QuoteHistoryTable, QuoteHistoryTableRef } from './QuoteHistoryTable';
import { Alert, Drawer, Button, Message, Modal } from '@bedrock/components';
import withFunctionCall from '@mpc/common/src/utils/withFunctionCall';
import { Upload as UploadIcon } from '@bedrock/icons-react';
import { useImportFileModal } from '@mpc/common/src/features/importFileModal';
import { AdvancePriceCheck } from 'services/api';

/** 运行时列表项可能携带 biz_id/biz_type/demand_title 等跨表字段（后端返回），但自动生成类型未收录 */
type AdvancePriceCheckListItem = AdvancePriceCheck & {
  biz_id?: number | string;
  biz_type?: string;
  title?: string;
};

interface Props {
  /** 整条列表行数据；抽屉内上传需要 biz_id / biz_type 构造 importType */
  record?: AdvancePriceCheckListItem;
  /** 上传成功后通知外层刷新列表 */
  onUploadSuccess?: () => void;
  onClose?: () => void;
  showUploadButton?: boolean;
}

const AdvancePriceCheckDetailDrawer: React.FC<Props> = ({
  record,
  onUploadSuccess,
  onClose,
  showUploadButton = true,
}) => {
  const advancePriceCheckId = record?.id;
  const historyTableRef = useRef<QuoteHistoryTableRef>(null);

  /** Drawer 标题：#{单据id} {需求标题} - {创建日期} */
  const drawerTitle = useMemo(() => {
    const parts: string[] = [];
    if (record?.biz_id) parts.push(`#${record.biz_id}`);
    if (record?.title) parts.push(record.title);
    const head = parts.join(' ');
    const date = record?.created_at ? dayjs(record.created_at).format('YYYY-MM-DD') : '';
    return date ? (head ? `${head} - ${date}` : date) : head || '提前核价详情';
  }, [record]);

  const { openImportFileModal, closeImportFileModal } = useImportFileModal({});

  const handleClose = useMemoizedFn(() => {
    onClose?.();
  });

  /** 打开导入弹窗 */
  const handleUpload = useMemoizedFn(() => {
    if (!record) return;
    const importType = `advance_price_check$${record.biz_id}_${record.biz_type}`;

    openImportFileModal({
      topSlot: (
        <Alert
          message="上传初步报价后，将进行内部核价，期间不可重新上传；核价结束或核价废弃后，可重新上传新的报价附件"
          type="info"
          showIcon
        />
      ),
      importType,
      importTitle: '导入报价方案',
      showSuccessToast: false,
      uploadFinishedCallback: (data) => {
        if (data.code === 1 && data?.data?.fail_count === 0) {
          // 成功场景
          closeImportFileModal();
          Message.success('报价已提交，智能核价中');
          // 刷新历史报价表
          historyTableRef.current?.refresh();
          onUploadSuccess?.();
        }
      },
    });
  });

  return (
    <Drawer
      visible
      onClose={handleClose}
      title={drawerTitle}
      width={1080}
      destroyOnClose
      bodyScrolled
      bodyStyle={{ backgroundColor: '#f5f5f5' }}
      footer={
        <div className="flex justify-end items-center gap-2">
          {showUploadButton && (
            <Button type="primary" onClick={handleUpload}>
              提交报价
            </Button>
          )}
        </div>
      }
      closable>
      <div className="flex flex-col gap-4">
        {/* 初步报价历史表 */}
        <QuoteHistoryTable ref={historyTableRef} advancePriceCheckId={advancePriceCheckId} />
      </div>
    </Drawer>
  );
};

export default withFunctionCall(AdvancePriceCheckDetailDrawer);
